import eventBus from '../event_bus'

const observer = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    entry.target.intersected = entry.isIntersecting
    entry.target.tryFetchContent()
  })
})

class CarwowRemoteContent extends HTMLElement {
  static get RETRYABLE_STATUS_CODES () {
    return [408, 503, 504, 599]
  }

  static get DELAYS () {
    return [250, 500, 1000, 2000, 4000]
  }

  static get MAX_RETRIES () {
    return this.DELAYS.length
  }

  static get observedAttributes () {
    return ['href', 'fetch']
  }

  get fetch () {
    return this.hasAttribute('fetch')
  }

  set fetch (val) {
    if (val) {
      this.setAttribute('fetch', '')
    } else {
      this.removeAttribute('fetch')
    }
  }

  get failed () {
    return this.hasAttribute('failed')
  }

  set failed (val) {
    if (val) {
      this.setAttribute('failed', '')
    } else {
      this.removeAttribute('failed')
    }
  }

  get link () {
    return this.getAttribute('href')
  }

  get id () {
    return this.getAttribute('data-remote-content-event-id')
  }

  tryFetchContent () {
    if (this.intersected && (this.fetch || this.isVisible())) {
      this.failed = false
      this.fetchContent()
    }
  }

  isVisible () {
    return window.getComputedStyle(this).visibility !== 'hidden'
  }

  observeRemoteContentViewed (elInView) {
    const viewContentEventEmitterName = this.id ? `remote_content:viewedContent:${this.id}` : 'remote_content:viewedContent'

    const remoteContentViewedObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          remoteContentViewedObserver.disconnect()
          eventBus.emit(viewContentEventEmitterName, this)
        }
      })
    }, { threshold: 0.5 })

    remoteContentViewedObserver.observe(elInView)
  }

  fetchContent (attempts = 0) {
    if (!this.fetched && this.link !== null) {
      this.fetched = true
      fetch(this.link, { credentials: 'same-origin' })
        .then(response => {
          if (response.ok) {
            return response.text()
          }
          const error = new Error(response.statusText)
          error.httpStatus = response.status
          throw error
        })
        .then(body => {
          const contentDisplayedEventEmitterName = this.id ? `remote_content:contentLoaded:${this.id}` : 'remote_content:contentLoaded'

          this.innerHTML = body
          eventBus.emit(contentDisplayedEventEmitterName, this)
          this.observeRemoteContentViewed(this)
        })
        .catch(error => {
          if (this.isRetryable(error.httpStatus)) {
            this.retry(attempts)
          }
        })
    }
  }

  isRetryable (status) {
    return this.constructor.RETRYABLE_STATUS_CODES.includes(status)
  }

  retry (attempts) {
    const currentRetry = attempts + 1
    if (currentRetry > this.constructor.MAX_RETRIES) {
      this.failed = true
    } else {
      const delay = this.constructor.DELAYS[attempts]
      setTimeout(() => {
        this.fetched = false
        this.fetchContent(currentRetry)
      }, delay)
    }
  }

  connectedCallback () {
    observer.observe(this)
  }

  disconnectedCallback () {
    observer.unobserve(this)
    this.intersected = false
  }

  attributeChangedCallback (name, oldValue, newValue) {
    if (name === 'href' && oldValue !== newValue) {
      this.fetched = false
    }

    this.tryFetchContent()
  }
}

function initRemoteContent () {
  if (window.customElements.get('carwow-remote-content')) { return }
  window.customElements.define('carwow-remote-content', CarwowRemoteContent)
}

initRemoteContent()
